// frontend/src/pages/MoviesPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Filter, Star, X, ChevronDown, Loader } from 'lucide-react';
import MovieCard from '../components/MovieCard';
import { moviesAPI } from '../services/api';
import { ROMCOM_TROPES, MOOD_CATEGORIES, DECADES } from '../utils/constants';

interface Movie {
  id: number;
  title: string;
  overview?: string;
  posterUrl?: string;
  releaseYear?: number;
  voteAverage?: number;
  season?: string;
  holiday?: string;
  moods?: string[];
  tropes?: string[];
  favoritesCount?: number;
  reviewsCount?: number;
}

interface Pagination {
  total: number;
  page: number;
  totalPages: number;
  hasMore: boolean;
}

const seasons = ['winter', 'spring', 'summer', 'autumn'];
const holidays = ['christmas', 'valentine', 'new_year'];
const sortOptions = [
  { value: 'popularity', label: 'Most Popular' },
  { value: 'rating', label: 'Highest Rated' },
  { value: 'newest', label: 'Newest First' },
  { value: 'title', label: 'A–Z' },
];

const MoviesPage: React.FC = () => {
  const [movies, setMovies] = useState<Movie[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState('popularity');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalMovies, setTotalMovies] = useState(0);

  const [activeFilters, setActiveFilters] = useState({
    season: '',
    holiday: '',
    mood: '',
    trope: '',
    minRating: 0,
    decade: '',
  });

  // ─────────────────────────────────────────────
  // FETCH MOVIES
  // ─────────────────────────────────────────────
  const fetchMovies = useCallback(async (page: number, append: boolean = false) => {
    try {
      append ? setIsLoadingMore(true) : setIsLoading(true);
      setError('');

      const params: Record<string, any> = {
        page,
        limit: 20,
        sortBy,
      };

      if (searchQuery) params.search = searchQuery;
      if (activeFilters.season) params.season = activeFilters.season;
      if (activeFilters.holiday) params.holiday = activeFilters.holiday;
      if (activeFilters.mood) params.mood = activeFilters.mood.toLowerCase().replace('-', '_').replace(' ', '_');
      if (activeFilters.trope) params.trope = activeFilters.trope;
      if (activeFilters.minRating > 0) params.minRating = activeFilters.minRating * 2; // convert 0-5 to 0-10
      if (activeFilters.decade) params.decade = parseInt(activeFilters.decade);

      const response = await moviesAPI.getMovies(params);
      const { movies: newMovies, pagination: newPagination } = response.data.data;

      if (append) {
        setMovies(prev => [...prev, ...newMovies]);
      } else {
        setMovies(newMovies);
        setTotalMovies(newPagination.total);
      }

      setPagination(newPagination);
    } catch (err) {
      setError('Failed to load movies. Please try again.');
      console.error('Fetch movies error:', err);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [searchQuery, sortBy, activeFilters]);

  // Fetch on filter/sort/search change
  useEffect(() => {
    setCurrentPage(1);
    fetchMovies(1, false);
  }, [fetchMovies]);

  // ─────────────────────────────────────────────
  // SEARCH (debounced)
  // ─────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ─────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────
  const handleFilterChange = (key: keyof typeof activeFilters, value: string | number) => {
    setActiveFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setActiveFilters({ season: '', holiday: '', mood: '', trope: '', minRating: 0, decade: '' });
    setSearchInput('');
    setSearchQuery('');
    setSortBy('popularity');
  };

  const loadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchMovies(nextPage, true);
  };

  const activeFilterCount = Object.values(activeFilters).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#faf8f6]">

      <div className="container mx-auto px-4 pt-24 pb-16">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="text-5xl font-playfair font-bold text-gray-800 mb-4">
            Romcom Database
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl">
            Browse {totalMovies > 0 ? `${totalMovies.toLocaleString()}+` : 'thousands of'} romantic comedies.
            Filter by season, mood, decade, or trope to find your perfect match.
          </p>
        </motion.div>

        {/* Search + Filter Bar */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row gap-4 mb-4">

            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search movies, directors, or actors..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-[#faf8f6] rounded-xl border border-gray-200 focus:border-romcom-red focus:ring-2 focus:ring-romcom-red/20 focus:outline-none shadow-sm"
              />
            </div>

            {/* Sort */}
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none pl-4 pr-10 py-3 bg-[#faf8f6] rounded-xl border border-gray-200 focus:border-romcom-red focus:outline-none shadow-sm text-gray-700 cursor-pointer"
              >
                {sortOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-6 py-3 rounded-xl font-medium flex items-center justify-center space-x-2 transition-all ${
                showFilters ? 'bg-romcom-red text-white' : 'bg-[#faf8f6] text-gray-700 border border-gray-200'
              }`}
            >
              <Filter className="w-5 h-5" />
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Clear */}
            {(activeFilterCount > 0 || searchInput) && (
              <button
                onClick={clearFilters}
                className="px-6 py-3 rounded-xl font-medium bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 flex items-center space-x-2"
              >
                <X className="w-5 h-5" />
                <span>Clear</span>
              </button>
            )}
          </div>

          {/* Filters Panel */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
              >
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

                    {/* Season */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Season</label>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handleFilterChange('season', '')}
                          className={`px-3 py-1 rounded-full text-sm ${activeFilters.season === '' ? 'bg-romcom-red text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                        >
                          All
                        </button>
                        {seasons.map(s => (
                          <button
                            key={s}
                            onClick={() => handleFilterChange('season', s)}
                            className={`px-3 py-1 rounded-full text-sm capitalize ${activeFilters.season === s ? 'bg-romcom-red text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                          >
                            {s === 'winter' ? '❄️' : s === 'spring' ? '🌸' : s === 'summer' ? '☀️' : '🍂'} {s}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Holiday */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Holiday</label>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handleFilterChange('holiday', '')}
                          className={`px-3 py-1 rounded-full text-sm ${activeFilters.holiday === '' ? 'bg-romcom-red text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                        >
                          All
                        </button>
                        {holidays.map(h => (
                          <button
                            key={h}
                            onClick={() => handleFilterChange('holiday', h)}
                            className={`px-3 py-1 rounded-full text-sm ${activeFilters.holiday === h ? 'bg-romcom-red text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                          >
                            {h === 'christmas' ? '🎄' : h === 'valentine' ? '💝' : '🎆'} {h.replace('_', ' ')}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Trope */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Trope</label>
                      <select
                        value={activeFilters.trope}
                        onChange={(e) => handleFilterChange('trope', e.target.value)}
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-romcom-red focus:border-transparent"
                      >
                        <option value="">All Tropes</option>
                        {ROMCOM_TROPES.map(trope => (
                          <option key={trope.id} value={trope.name}>
                            {trope.icon} {trope.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Mood */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Mood</label>
                      <select
                        value={activeFilters.mood}
                        onChange={(e) => handleFilterChange('mood', e.target.value)}
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-romcom-red focus:border-transparent"
                      >
                        <option value="">All Moods</option>
                        {MOOD_CATEGORIES.map(mood => (
                          <option key={mood.id} value={mood.name}>
                            {mood.icon} {mood.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Decade */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Decade</label>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handleFilterChange('decade', '')}
                          className={`px-3 py-1 rounded-full text-sm ${activeFilters.decade === '' ? 'bg-romcom-red text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                        >
                          All
                        </button>
                        {DECADES.map(decade => (
                          <button
                            key={decade.id}
                            onClick={() => handleFilterChange('decade', decade.years.split('-')[0])}
                            className={`px-3 py-1 rounded-full text-sm ${activeFilters.decade === decade.years.split('-')[0] ? 'bg-romcom-red text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                          >
                            {decade.icon} {decade.name}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Rating */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Minimum Rating: {activeFilters.minRating > 0 ? `${activeFilters.minRating}+ ⭐` : 'Any'}
                      </label>
                      <div className="flex items-center space-x-4">
                        <input
                          type="range"
                          min="0"
                          max="5"
                          step="0.5"
                          value={activeFilters.minRating}
                          onChange={(e) => handleFilterChange('minRating', parseFloat(e.target.value))}
                          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-romcom-red"
                        />
                        <div className="flex items-center">
                          <Star className="w-4 h-4 text-yellow-400 fill-current mr-1" />
                          <span className="font-medium">{activeFilters.minRating}</span>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Results Info */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              {isLoading ? 'Loading...' : `${pagination?.total?.toLocaleString() || 0} Movies Found`}
            </h2>
            {/* Active filter tags */}
            {activeFilterCount > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {activeFilters.season && (
                  <span className="bg-romcom-pink/20 text-romcom-red px-3 py-1 rounded-full text-sm capitalize">
                    {activeFilters.season}
                  </span>
                )}
                {activeFilters.holiday && (
                  <span className="bg-romcom-pink/20 text-romcom-red px-3 py-1 rounded-full text-sm capitalize">
                    {activeFilters.holiday.replace('_', ' ')}
                  </span>
                )}
                {activeFilters.mood && (
                  <span className="bg-romcom-pink/20 text-romcom-red px-3 py-1 rounded-full text-sm capitalize">
                    {activeFilters.mood}
                  </span>
                )}
                {activeFilters.trope && (
                  <span className="bg-romcom-pink/20 text-romcom-red px-3 py-1 rounded-full text-sm">
                    {activeFilters.trope}
                  </span>
                )}
                {activeFilters.minRating > 0 && (
                  <span className="bg-romcom-pink/20 text-romcom-red px-3 py-1 rounded-full text-sm">
                    {activeFilters.minRating}+ stars
                  </span>
                )}
                {activeFilters.decade && (
                  <span className="bg-romcom-pink/20 text-romcom-red px-3 py-1 rounded-full text-sm">
                    {activeFilters.decade}s
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="text-gray-500 text-sm">
            Showing {movies.length} of {pagination?.total?.toLocaleString() || 0}
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-24">
            <Loader className="w-10 h-10 text-romcom-red animate-spin mb-4" />
            <p className="text-gray-500">Finding your perfect romcoms...</p>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="text-center py-16">
            <div className="text-5xl mb-4">😔</div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">{error}</h3>
            <button
              onClick={() => fetchMovies(1)}
              className="mt-4 px-6 py-3 bg-romcom-red text-white rounded-full hover:bg-red-600 transition-all"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Movies Grid */}
        {!isLoading && !error && (
          <>
            {movies.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {movies.map((movie, index) => (
                  <motion.div
                    key={movie.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(index * 0.03, 0.5) }}
                  >
                    <MovieCard movie={movie} />
                  </motion.div>
                ))}
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-16"
              >
                <div className="text-6xl mb-4">🎬</div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">No movies found</h3>
                <p className="text-gray-600 mb-6">Try adjusting your search or filters</p>
                <button
                  onClick={clearFilters}
                  className="px-6 py-3 bg-romcom-red text-white rounded-full hover:bg-red-600 transition-all"
                >
                  Clear All Filters
                </button>
              </motion.div>
            )}

            {/* Load More */}
            {pagination?.hasMore && (
              <div className="text-center mt-12">
                <button
                  onClick={loadMore}
                  disabled={isLoadingMore}
                  className="px-8 py-3 bg-white border-2 border-romcom-red text-romcom-red rounded-full font-medium hover:bg-romcom-red hover:text-white transition-all disabled:opacity-50 flex items-center gap-2 mx-auto"
                >
                  {isLoadingMore ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    `Load More Movies (${(pagination.total - movies.length).toLocaleString()} remaining)`
                  )}
                </button>
              </div>
            )}
          </>
        )}

        {/* Stats */}
        {!isLoading && totalMovies > 0 && (
          <div className="mt-16 pt-8 border-t border-gray-200">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">Romcom Database Stats</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="bg-[#faf8f6] p-6 rounded-xl shadow-sm border border-romcom-pink/20 text-center">
                <div className="text-3xl font-bold text-romcom-red mb-2">{totalMovies.toLocaleString()}</div>
                <div className="text-gray-600">Total Movies</div>
              </div>
              <div className="bg-[#faf8f6] p-6 rounded-xl shadow-sm border border-romcom-pink/20 text-center">
                <div className="text-3xl font-bold text-romcom-red mb-2">🎄</div>
                <div className="text-gray-600">Christmas Romcoms</div>
              </div>
              <div className="bg-[#faf8f6] p-6 rounded-xl shadow-sm border border-romcom-pink/20">
                <div className="text-3xl font-bold text-romcom-red mb-2">💝</div>
                <div className="text-gray-600">Valentine Films</div>
              </div>
              <div className="bg-[#faf8f6] p-6 rounded-xl shadow-sm border border-romcom-pink/20">
                <div className="text-3xl font-bold text-romcom-red mb-2">🌍</div>
                <div className="text-gray-600">Languages</div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default MoviesPage;